import type { PartialBlock } from '@blocknote/core';
import * as React from 'react';

function collectTableVariants(
  blocks: PartialBlock[] | undefined,
  variants: string[] = [],
): string[] {
  blocks?.forEach((block) => {
    if (block.type === 'table') {
      variants.push(
        ((block.props as Record<string, unknown> | undefined)?.[
          'tableVariant'
        ] as string | undefined) ?? '',
      );
    }

    collectTableVariants(
      Array.isArray(block.children) ? block.children : undefined,
      variants,
    );
  });

  return variants;
}

/**
 * Builds a map from table block ID to its `tableVariant` value.
 * Used to re-apply variants that BlockNote strips from its internal state.
 */
export function buildTableVariantMap(
  blocks: PartialBlock[] | undefined,
): Map<string, string> {
  const map = new Map<string, string>();

  function walk(items: PartialBlock[] | undefined): void {
    items?.forEach((block) => {
      if (block.type === 'table' && block.id) {
        const variant = (
          block.props as Record<string, unknown> | undefined
        )?.['tableVariant'] as string | undefined;
        if (variant) {
          map.set(block.id, variant);
        }
      }
      walk(Array.isArray(block.children) ? block.children : undefined);
    });
  }

  walk(blocks);
  return map;
}

/**
 * Re-applies `tableVariant` props to table blocks in `outputBlocks` using
 * the variant map built from initial content. BlockNote strips unknown props
 * from its internal state, so this restores them before saving.
 */
export function applyTableVariants(
  outputBlocks: PartialBlock[],
  variantMap: Map<string, string>,
): PartialBlock[] {
  if (variantMap.size === 0) return outputBlocks;

  function walk(blocks: PartialBlock[]): PartialBlock[] {
    return blocks.map((block) => {
      const children = Array.isArray(block.children)
        ? walk(block.children)
        : block.children;

      if (block.type === 'table' && block.id && variantMap.has(block.id)) {
        return {
          ...block,
          props: {
            ...(block.props as Record<string, unknown>),
            tableVariant: variantMap.get(block.id),
          } as PartialBlock['props'],
          children,
        } as PartialBlock;
      }

      return children !== block.children
        ? ({ ...block, children } as PartialBlock)
        : block;
    });
  }

  return walk(outputBlocks);
}

function tagRenderedTables(
  wrapper: HTMLDivElement,
  tableVariants: string[],
): void {
  wrapper.querySelectorAll('table').forEach((table, index) => {
    const variant = tableVariants[index];

    if (variant) {
      table.dataset['tableVariant'] = variant;
      return;
    }

    delete table.dataset['tableVariant'];
  });
}

/**
 * Tags rendered `<table>` elements with `data-table-variant` attributes that
 * correspond to the variant metadata stored in BlockNote blocks. Uses a
 * MutationObserver to re-tag after DOM mutations.
 */
export function useTableVariantTagging(
  wrapperRef: React.RefObject<HTMLDivElement | null>,
  initialContent: PartialBlock[] | undefined,
): void {
  const tableVariants = React.useMemo(
    () => collectTableVariants(initialContent),
    [initialContent],
  );

  React.useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper || tableVariants.length === 0) {
      return;
    }

    tagRenderedTables(wrapper, tableVariants);

    const observer = new MutationObserver(() => {
      tagRenderedTables(wrapper, tableVariants);
    });
    observer.observe(wrapper, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [tableVariants, wrapperRef]);
}
